<template>
  <div class="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 overflow-y-auto flex items-center justify-center p-4" @click.self="closeModal">
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md relative animate-modal-in">
      <!-- Header with gradient -->
      <div class="bg-gradient-to-r from-green-500 to-emerald-600 rounded-t-2xl px-6 py-5 sm:px-8 sm:py-6">
        <button @click="closeModal" class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors">
          <X :size="18" />
        </button>
        <h3 class="text-xl sm:text-2xl font-bold text-white">Agende uma Demonstração</h3>
        <p class="text-green-100 text-sm mt-1">Veja como a AutoVolt pode transformar seu atendimento</p>
      </div>

      <div class="p-6 sm:p-8">
        <div v-if="!successMessage">
          <form @submit.prevent="handleSubmit">
            <div class="space-y-4">
              <div>
                <label for="name" class="block text-sm font-semibold text-gray-700 mb-1.5">Nome Completo</label>
                <input type="text" id="name" v-model="formData.name" required placeholder="Seu nome" class="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all">
              </div>

              <div>
                <label for="email" class="block text-sm font-semibold text-gray-700 mb-1.5">Email Corporativo</label>
                <input type="email" id="email" v-model="formData.email" required placeholder="seu@email.com" class="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all">
              </div>

              <div>
                <label for="phone" class="block text-sm font-semibold text-gray-700 mb-1.5">Telefone (WhatsApp)</label>
                <vue-tel-input
                  id="phone"
                  v-model="formData.phone"
                  :inputOptions="{ required: true }"
                  mode="international"
                  defaultCountry="BR"
                  @validate="onPhoneValidate"
                  :disabled-formatting="true"
                ></vue-tel-input>
              </div>

              <div>
                <label for="company" class="block text-sm font-semibold text-gray-700 mb-1.5">Nome da Empresa</label>
                <input type="text" id="company" v-model="formData.company" required placeholder="Sua empresa" class="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all">
              </div>
            </div>

            <div class="mt-6">
              <button type="submit" :disabled="loading" class="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3.5 px-6 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <span>{{ loading ? 'Enviando...' : 'Enviar Solicitação' }}</span>
                <svg v-if="!loading" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </button>
            </div>
            <p v-if="errorMessage" class="text-red-500 text-sm mt-4 text-center bg-red-50 p-3 rounded-lg">{{ errorMessage }}</p>
          </form>
        </div>

        <!-- Success State -->
        <div v-else class="text-center py-4">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h3 class="text-2xl font-bold text-gray-900 mb-2">Obrigado!</h3>
          <p class="text-gray-600 mb-6">{{ successMessage }}</p>
          <button @click="closeModal" class="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg shadow-green-500/25">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { X } from 'lucide-vue-next';
import axios from 'axios';
import { trackFacebookEvent } from '../../utils/facebook-pixel';

const emit = defineEmits(['close']);

const formData = ref({
  name: '',
  email: '',
  phone: '',
  company: ''
});

const phoneValid = ref(false);

const onPhoneValidate = (payload: any) => {
  phoneValid.value = payload.valid;
};

const loading = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const closeModal = () => {
  emit('close');
};

const handleSubmit = async () => {
  if (!phoneValid.value) {
    errorMessage.value = 'Por favor, insira um número de telefone válido.';
    return;
  }

  loading.value = true;
  errorMessage.value = '';
  try {
    const payload = {
      ...formData.value,
      phone: formData.value.phone.replace(/\D/g, ''),
    };
    await axios.post('https://auto.autovolt.ai/webhook/leads/site', payload);
    
    // Track Facebook Pixel Lead event
    trackFacebookEvent('Lead', {
      content_name: 'Demo Request',
      value: 0,
      currency: 'BRL'
    });

    successMessage.value = 'Sua solicitação foi enviada com sucesso. Entraremos em contato em breve.';
  } catch (error) {
    console.error('Erro ao enviar formulário:', error);
    errorMessage.value = 'Ocorreu um erro ao enviar sua solicitação. Por favor, tente novamente.';
  } finally {
    loading.value = false;
  }
};

// Usa uma classe no <html> para travar o scroll, uma abordagem mais limpa
// que evita a manipulação direta de estilos.
onMounted(() => {
  document.documentElement.classList.add('modal-open');
});

onUnmounted(() => {
  document.documentElement.classList.remove('modal-open');
});
</script>

<style scoped>
/* Modal animation */
.animate-modal-in {
  animation: modalIn 0.2s ease-out;
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Scoped styles for vue-tel-input */
:deep(.vue-tel-input) {
  border: 1px solid #e5e7eb; /* border-gray-200 */
  border-radius: 0.75rem; /* rounded-xl */
  background-color: #f9fafb; /* bg-gray-50 */
  transition: border-color 0.2s, box-shadow 0.2s;
}

:deep(.vue-tel-input:focus-within) {
  border-color: #22c55e; /* focus:border-green-500 */
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1); /* focus:ring-green-500/20 */
}

:deep(.vti__input) {
  border: none !important;
  box-shadow: none !important;
  height: 46px; /* Match other inputs */
  font-size: 0.875rem;
  background: transparent;
}

:deep(.vti__dropdown) {
  padding-left: 0.75rem;
  border-radius: 0.75rem 0 0 0.75rem;
}
</style>
